import { useState, useContext, useMemo } from "react";
import FilterRow2 from "./components/FilterRow2";
import { useAnonymousData } from "../StudentData/hooks/useAnonymousData";
import { StudentDataContext } from "../../context/studentData.context";
import { FaCircle } from "react-icons/fa";
import { Trends } from "./components/Trends";
import { TrendsSkeleton } from "./components/TrendsSkeleton";
import StudentDataWellnessSuccessTab2 from "./components/StudentDataWellnessSuccessTab2";
import Recommendations from "./components/Recommendations";
import ScreenshotButtonComponent2 from "./components/ScreenshotButtonComponent2";
import { buildExportFilters } from "@/utils/analytics/exportFilters";
import NoData from "./components/NoData";
import EmotionsDistribution from "./components/EmotionsDistribution";
import { Search } from "lucide-react";
import { useOnboardingUiEnabled } from "@/hooks/useOnboardingUiEnabled";
import { useIsMobileViewport } from "@/hooks/useIsMobileViewport";
import { useBootstrap } from "@/features/bootstrap";

export default function EducatorDashboard(props) {
  const { data: user } = useBootstrap();
  const [isExpanded, setIsExpanded] = useState(false);
  const contextData = useContext(StudentDataContext);

  const anonymousData = useAnonymousData(user, props.anonamizeData);
  const { state, updateState } = props.anonamizeData
    ? anonymousData
    : contextData;
  
  let whoFilter = "";

  if (state.selectedGroupType === "district") {
    whoFilter = state.chosenDistrict;
  } else if (state.selectedGroupType === "school") {
    whoFilter =
      state.chosenSchool === "All Schools"
        ? state.chosenDistrict
        : state.chosenSchool;
  } else if (state.selectedGroupType === "class") {
    whoFilter =
      state.chosenClass === "All Classes"
        ? state.chosenSchool === "All Schools"
          ? state.chosenDistrict
          : state.chosenSchool
        : state.chosenClass;
  } else if (state.selectedGroupType === "student") {
    const email = state.chosenStudent;
    whoFilter =
      state.studentEmailToNameMap && state.studentEmailToNameMap[email]
        ? state.studentEmailToNameMap[email]
        : email;
  }

  const emotionData = state?.counts || [];
  const emotionPercents = useMemo(() => {
    const totalCurrent = emotionData.reduce(
      (sum, d) => sum + (d.current || 0),
      0,
    );
    const totalCompare = emotionData.reduce(
      (sum, d) => sum + (d.compare || 0),
      0,
    );

    return emotionData.map((d) => {
      const currentPercent =
        totalCurrent > 0 ? Math.round((d.current / totalCurrent) * 100) : 0;
      const comparePercent =
        totalCompare > 0 ? Math.round((d.compare / totalCompare) * 100) : 0;
      const change = currentPercent - comparePercent;
      return {
        ...d,
        currentPercent,
        comparePercent,
        change,
      };
    });
  }, [emotionData]);

  const [showSearchModal, setShowSearchModal] = useState(false);
  const isTourActive = useOnboardingUiEnabled();
  const isMobileViewport = useIsMobileViewport();
  const demoImgSrc = (src) =>
    isMobileViewport ? src.replace(/-demo(\.[a-z]+)$/i, "-mobile$1") : src;

  const showTourDemoWidgets =
    isTourActive &&
    state.wellnessLoadedPeriods?.["2months"] === true &&
    (state.currentStudents.length === 0 ||
      (state.actionsCounter <= 0 &&
        (state.chosenStudent === "All Students" ||
          state.chosenStudent === "Choose Student")));

  const DemoWidget = ({ src, alt, className = "" }) => (
    <div className={"bg-white shadow-md rounded p-3 sm:p-4 2xl:p-8 " + className}>
      <div className="inline-flex items-center rounded-md bg-amber-50 px-2 py-1 text-[11px] font-semibold text-amber-900 ring-1 ring-amber-200">
        Example with demo student data
      </div>
      <img
        src={demoImgSrc(src)}
        alt={alt}
        className="mt-2 w-full object-contain rounded-xl border border-black/10 bg-white"
        loading="lazy"
        draggable={false}
      />
    </div>
  );

  const WhatHelps = (props) => {
    return (
      <div>
        <StudentDataWellnessSuccessTab2
          state={state}
          userRole={user?.role}
          updateState={updateState}
          expanded={isExpanded}
          setExpanded={setIsExpanded}
        />
      </div>
    );
  };

  return (
    // wrap main component with tw container to apply Tailwind styles
    <div className="tw">
      <div
        className="sm:ml-[130px] sm:mt-0 mt-16 border border-[#DDDDDD] bg-[#F4F4F3] flex flex-col"
      >
        {/* Web Header */}
        <div className="hidden sm:flex justify-between items-center w-full gap-3 shadow px-3 py-3 sm:px-8 sm:py-4 bg-[#FFFFFF] sticky top-0 z-10">
          <div>
            <FilterRow2
              state={state}
              userRole={user?.role}
              updateState={updateState}
              user={user}
            />
          </div>
          <div className="lg:mr-3">
            <ScreenshotButtonComponent2
              user={user}
              exportFilters={buildExportFilters(state)}
            />
          </div>
        </div>
        <div
          className="px-3 sm:px-8 pb-10 sm:pt-6 pt-3 flex-1"
          style={{ position: "relative" }}
          data-onboarding-id="educator-who"
        >
          <div className="hidden sm:flex justify-between items-center border-b">
            <div className="flex items-center space-x-4 text-[24px] ">
              <span className="ml-1 mr-1.5 text-500 font-bold capitalize">
                {whoFilter}
                {whoFilter && ":"}
              </span>
              Wellness Data
              <FaCircle className="w-2.5 h-2.5 text-600" />
              <span>
                {state.currentStudents.length}{" "}
                {state.currentStudents.length <= 1 ? "student" : "students"}
              </span>
            </div>
          </div>
          {/* Mobile Header */}
          <div className="sm:hidden flex flex-col gap-1">
            <div className="flex justify-between items-center">
              <div className="text-[22px] font-bold">Wellness Data</div>
              <button
                onClick={() => setShowSearchModal(true)}
                className="text-base border border-[#DDDDDD] rounded px-2 py-1 flex items-center gap-2"
                data-analytics-id="open_search_modal"
                data-analytics-label="Open search"
              >
                Search <Search className="w-4 h-4" />
              </button>
              {showSearchModal && (
                <div className="fixed inset-0 z-50 bg-white flex flex-col px-4 py-6 overflow-y-auto animate-fadeIn">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-semibold">Search</h2>
                    <button
                      onClick={() => setShowSearchModal(false)}
                      className="text-gray-600 text-lg"
                    >
                      ✕
                    </button>
                  </div>
                  <FilterRow2
                    state={state}
                    userRole={user?.role}
                    updateState={updateState}
                    onClose={() => setShowSearchModal(false)}
                    user={user}
                  />
                </div>
              )}
            </div>
            <div className="text-lg opacity-75 mb-1">
              {state.currentStudents.length}{" "}
              {state.currentStudents.length === 1 ? "student" : "students"}
            </div>
          </div>
          <div className="sm:hidden text-base border border-[#DDDDDD] rounded p-2 ">
            <span className="font-bold">Showing:</span> Wellness Data for{" "}
            <span className="capitalize font-bold">{whoFilter}</span> in the
            last {state.chosenTime}
          </div>

          {state.wellnessLoadedPeriods?.["2months"] === true && (
            <NoData
              hasStudents={state.currentStudents.length > 0}
              hasActivity={state.actionsCounter > 0}
              chosenStudent={state.chosenStudent}
              onboardingNoDataId="educator-dashboard"
            />
          )}
          {!state.wellnessLoadedPeriods?.["2months"] ? (
            <TrendsSkeleton />
          ) : showTourDemoWidgets ? (
            <DemoWidget
              src="/images/onboardingDemo/studentTrends-demo.png"
              alt="Educator Dashboard trends example (demo only)"
              className="mt-3"
            />
          ) : (
            <Trends
              state={state}
              updateState={updateState}
              emotionPercents={emotionPercents}
            />
          )}
          <div className="flex xl:flex-row flex-col gap-3 mt-3">
            <div className="flex-1 flex flex-col gap-3">
              <div className="flex-1 flex" data-onboarding-id="educator-emotions-breakdown">
                {showTourDemoWidgets ? (
                  <DemoWidget
                    src="/images/onboardingDemo/emotions-distribution-demo.png"
                    alt="Educator Dashboard emotions distribution example (demo only)"
                  />
                ) : (
                  <EmotionsDistribution
                    state={state}
                    updateState={updateState}
                    emotionPercents={emotionPercents}
                  />
                )}
              </div>
            </div>
            {/* Right Containers */}
            <div className="flex-1 flex flex-col gap-3">
              <div className="transition-all duration-300 ease-in-out relative">
                <div data-onboarding-id="educator-emotions-whats-helping">
                  {showTourDemoWidgets ? (
                    <DemoWidget
                      src="/images/onboardingDemo/whatHelps-demo.png"
                      alt="Educator Dashboard what’s helping example (demo only)"
                    />
                  ) : (
                    <WhatHelps
                      {...props}
                      state={state}
                      updateState={updateState}
                    />
                  )}
                </div>
              </div>
              {!isExpanded && (
                <div
                  className="transition-all duration-300 ease-in-out"
                  data-onboarding-id="educator-recommendations"
                >
                  {showTourDemoWidgets ? (
                    <DemoWidget
                      src="/images/onboardingDemo/recommendations-demo.png"
                      alt="Educator Dashboard recommendations example (demo only)"
                    />
                  ) : (
                    <Recommendations
                      state={state}
                      updateState={updateState}
                      emotionPercents={emotionPercents}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
